<template>
<div class="create-lead-container">
    <!-- Header -->
    <div class="header-container">
        <a href="#" class="brand w-nav-brand"><img sizes="84.03125px" alt="" :src="logo" loading="lazy" class="image-389" style="height: 45px;"></a>
        <h2 class="md-card-title">{{ AppName }} - Create New Lead</h2>
    </div>

    <div v-if="accessDenied" class="main-content">
        <div class="form-card md-card access-denied">
            <i class="material-icons access-denied-icon">lock</i>
            <h3>This URL is not accessible.</h3>
        </div>
    </div>

    <template v-else>
    <div v-if="loading" class="loading">
        <div class="md-loader"></div>
        <p>Creating lead...</p>
    </div>

    <div class="main-content">
        <div class="form-card md-card">
            <h3 class="md-card-subtitle">Lead Information</h3>
            
            <form @submit.prevent="submitLead" class="lead-form">
                <div class="form-row">
                    <div class="form-group">
                        <label for="first_name">First Name *</label>
                        <input 
                            id="first_name"
                            type="text" 
                            v-model="lead.first_name" 
                            class="md-input"
                            required
                            :class="{'invalid': errors.first_name}"
                        >
                        <span v-if="errors.first_name" class="error-text">{{ errors.first_name }}</span>
                    </div>

                    <div class="form-group">
                        <label for="last_name">Last Name *</label>
                        <input 
                            id="last_name"
                            type="text" 
                            v-model="lead.last_name" 
                            class="md-input"
                            required
                            :class="{'invalid': errors.last_name}"
                        >
                        <span v-if="errors.last_name" class="error-text">{{ errors.last_name }}</span>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group">
                        <label for="email">Email *</label>
                        <input 
                            id="email"
                            type="email" 
                            v-model="lead.email" 
                            class="md-input"
                            placeholder="example@email.com"
                            required
                            :class="{'invalid': errors.email}"
                        >
                        <span v-if="errors.email" class="error-text">{{ errors.email }}</span>
                    </div>

                    <div class="form-group">
                        <label for="phone">Phone *</label>
                        <input 
                            id="phone"
                            type="tel" 
                            v-model="lead.phone" 
                            class="md-input"
                            placeholder="5551234567"
                            maxlength="10"
                            required
                            :class="{'invalid': errors.phone}"
                        >
                        <span v-if="errors.phone" class="error-text">{{ errors.phone }}</span>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group">
                        <label for="gender">Gender *</label>
                        <select 
                            id="gender"
                            v-model="lead.gender" 
                            class="md-select"
                            required
                            :class="{'invalid': errors.gender}"
                        >
                            <option value="">Select Gender</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                         </select>
                        <span v-if="errors.gender" class="error-text">{{ errors.gender }}</span>
                    </div>

                    <div class="form-group">
                        <label for="state">State *</label>
                        <select 
                            id="state"
                            v-model="lead.state" 
                            class="md-select"
                            required
                            :class="{'invalid': errors.state}"
                        >
                            <option value="">Select State</option>
                            <option v-for="state in stateOptions" :key="state.value" :value="state.value">
                                {{ state.label }}
                            </option>
                        </select>
                        <span v-if="errors.state" class="error-text">{{ errors.state }}</span>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group">
                        <label for="weight">Current Weight (lbs) *</label>
                        <input 
                            id="weight"
                            type="number" 
                            v-model="lead.weight" 
                            class="md-input"
                            min="50"
                            max="800"
                            required
                            :class="{'invalid': errors.weight}"
                        >
                        <span v-if="errors.weight" class="error-text">{{ errors.weight }}</span>
                    </div>

                    <div class="form-group">
                        <label for="desired_weight">Desired Weight (lbs) *</label>
                        <input 
                            id="desired_weight"
                            type="number" 
                            v-model="lead.desired_weight" 
                            class="md-input"
                            min="50"
                            max="800"
                            required
                            :class="{'invalid': errors.desired_weight}"
                        >
                        <span v-if="errors.desired_weight" class="error-text">{{ errors.desired_weight }}</span>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group">
                        <div class="checkbox-group">
                            <label class="checkbox-label">
                                <input 
                                    type="checkbox" 
                                    v-model="lead.on_weight_loss_medication"
                                    class="checkbox-input"
                                >
                                <span class="checkbox-custom"></span>
                                Currently taking weight loss medication
                            </label>
                        </div>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group">
                        <label>Height *</label>
                        <div class="height-input-group">
                            <div class="height-field">
                                <input 
                                    id="height_feet"
                                    type="number" 
                                    v-model="lead.height_feet" 
                                    class="md-input"
                                    min="3"
                                    max="8"
                                    placeholder="5"
                                    required
                                    :class="{'invalid': errors.height_feet}"
                                >
                                <label for="height_feet" class="height-label">ft</label>
                            </div>
                            <div class="height-field">
                                <input 
                                    id="height_inches"
                                    type="number" 
                                    v-model="lead.height_inches" 
                                    class="md-input"
                                    min="0"
                                    max="11"
                                    placeholder="8"
                                    required
                                    :class="{'invalid': errors.height_inches}"
                                >
                                <label for="height_inches" class="height-label">in</label>
                            </div>
                        </div>
                        <span v-if="errors.height_feet || errors.height_inches" class="error-text">
                            {{ errors.height_feet || errors.height_inches }}
                        </span>
                    </div>

                    <div class="form-group">
                        <label for="dob">Date of Birth (MM/DD/YYYY) *</label>
                        <input 
                            id="dob"
                            type="text" 
                            v-model="lead.dob" 
                            class="md-input"
                            placeholder="MM/DD/YYYY"
                            required
                            :class="{'invalid': errors.dob}"
                            @input="formatDOB"
                        >
                        <span v-if="errors.dob" class="error-text">{{ errors.dob }}</span>
                    </div>
                </div>


                <!-- Validation Error Summary -->
                <div v-if="validationErrors.length > 0" class="validation-error-summary">
                    <div class="error-header">
                        <i class="material-icons">error</i>
                        <h4>Please fix the following issues:</h4>
                    </div>
                    <ul class="error-list">
                        <li v-for="error in validationErrors" :key="error">{{ error }}</li>
                    </ul>
                </div>

                <!-- Server Error Summary -->
                <div v-if="error" class="server-error-summary">
                    <div class="error-header">
                        <i class="material-icons">error</i>
                        <h4>Error:</h4>
                    </div>
                    <p class="server-error-message">{{ error }}</p>
                </div>

                <div class="form-actions">
                    <button type="button" @click="goBack" class="md-btn md-btn-secondary">
                        <i class="material-icons">arrow_back</i>
                        Cancel
                    </button>
                    <button type="submit" :disabled="submitting" class="md-btn md-btn-primary">
                        <i class="material-icons">add</i>
                        {{ submitting ? 'Creating...' : 'Create Lead' }}
                    </button>
                    <!-- Caller wants to sign up on their own: text them the signup link
                         for the number they dialed. Needs ?called_number= from the dialer. -->
                    <button v-if="calledNumber" type="button" @click="sendSignupLink"
                            :disabled="sendingLink || !lead.phone" class="md-btn md-btn-primary">
                        <i class="material-icons">{{ sendingLink ? 'hourglass_empty' : 'send' }}</i>
                        {{ sendingLink ? 'Sending...' : 'Send SMS Link' }}
                    </button>
                </div>
                <p v-if="linkStatus" :class="linkStatus === 'success' ? 'success-text' : 'error-text'">{{ linkStatusMessage }}</p>
            </form>
        </div>
    </div>

    <!-- Success Dialog -->
    <div v-if="showSuccessDialog" class="dialog-overlay">
        <div class="dialog-content md-dialog">
            <div class="md-dialog-header">
                <h3>Lead Created Successfully!</h3>
                <button @click="closeSuccessDialog" class="md-dialog-close">
                    <i class="material-icons">close</i>
                </button>
            </div>

            <div class="md-dialog-body">
                <div class="success-message">
                    <i class="material-icons success-icon">check_circle</i>
                    <p>Lead has been created successfully for <strong>{{ createdLead.first_name }} {{ createdLead.last_name }}</strong></p>
                    <p v-if="createdLead.id">Lead ID: {{ createdLead.id }}</p>
                </div>
            </div>

            <div class="md-dialog-footer">
                <button @click="createAnother" class="md-btn md-btn-secondary">Create Another</button>
                <button @click="viewLead" class="md-btn md-btn-primary" v-if="createdLead.id">
                    <i class="material-icons">visibility</i>
                    View Lead
                </button>
            </div>
        </div>
    </div>
    </template>
</div>
</template>

<script>
export default {
    name: 'CreateLead',
    data() {
        return {
            loading: false,
            error: null,
            submitting: false,
            showSuccessDialog: false,
            accessDenied: false,
            callCenterId: null,
            agentId: null,
            calledNumber: null,
            sendingLink: false,
            linkStatus: null,
            linkStatusMessage: '',
            prefilledPhone: '',
            logo: process.env.VUE_APP_LOGO,
            AppName: process.env.VUE_APP_NAME,
            lead: {
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
                gender: '',
                weight: '',
                desired_weight: '',
                on_weight_loss_medication: false,
                height_feet: '',
                height_inches: '',
                dob: '',
                source_web: '',
                state: ''
            },
            createdLead: {},
            errors: {},
            validationErrors: [],
            stateOptions: [
                { value: 'AL', label: 'Alabama' },
                { value: 'AK', label: 'Alaska' },
                { value: 'AZ', label: 'Arizona' },
                { value: 'AR', label: 'Arkansas' },
                { value: 'CA', label: 'California' },
                { value: 'CO', label: 'Colorado' },
                { value: 'CT', label: 'Connecticut' },
                { value: 'DE', label: 'Delaware' },
                { value: 'FL', label: 'Florida' },
                { value: 'GA', label: 'Georgia' },
                { value: 'HI', label: 'Hawaii' },
                { value: 'ID', label: 'Idaho' },
                { value: 'IL', label: 'Illinois' },
                { value: 'IN', label: 'Indiana' },
                { value: 'IA', label: 'Iowa' },
                { value: 'KS', label: 'Kansas' },
                { value: 'KY', label: 'Kentucky' },
                { value: 'ME', label: 'Maine' },
                { value: 'MD', label: 'Maryland' },
                { value: 'MA', label: 'Massachusetts' },
                { value: 'MI', label: 'Michigan' },
                { value: 'MN', label: 'Minnesota' },
                { value: 'MO', label: 'Missouri' },
                { value: 'MT', label: 'Montana' },
                { value: 'NE', label: 'Nebraska' },
                { value: 'NV', label: 'Nevada' },
                { value: 'NH', label: 'New Hampshire' },
                { value: 'NJ', label: 'New Jersey' },
                { value: 'NM', label: 'New Mexico' },
                { value: 'NY', label: 'New York' },
                { value: 'NC', label: 'North Carolina' },
                { value: 'ND', label: 'North Dakota' },
                { value: 'OH', label: 'Ohio' },
                { value: 'OK', label: 'Oklahoma' },
                { value: 'OR', label: 'Oregon' },
                { value: 'PA', label: 'Pennsylvania' },
                { value: 'RI', label: 'Rhode Island' },
                { value: 'SC', label: 'South Carolina' },
                { value: 'SD', label: 'South Dakota' },
                { value: 'TN', label: 'Tennessee' },
                { value: 'TX', label: 'Texas' },
                { value: 'UT', label: 'Utah' },
                { value: 'VT', label: 'Vermont' },
                { value: 'VA', label: 'Virginia' },
                { value: 'WA', label: 'Washington' },
                { value: 'WV', label: 'West Virginia' },
                { value: 'WI', label: 'Wisconsin' },
                { value: 'WY', label: 'Wyoming' }
            ]
        }
    },

    created() {
        const { phone, call_center_id, agent, called_number } = this.$route.query;
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

        if (!phone || !call_center_id || !uuidRegex.test(call_center_id)) {
            this.accessDenied = true;
            return;
        }

        let digits = String(phone).replace(/\D/g, '');
        if (digits.length === 11 && digits.startsWith('1')) {
            digits = digits.substring(1);
        }
        this.prefilledPhone = digits.substring(0, 10);
        this.lead.phone = this.prefilledPhone;
        this.callCenterId = call_center_id;
        this.agentId = agent ? String(agent) : null;
        this.calledNumber = called_number ? String(called_number) : null;
    },

    mounted() {
        // Load Material Icons if not already loaded
        if (!document.querySelector('link[href*="material-icons"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
            document.head.appendChild(link);
        }

        if (this.accessDenied) {
            return;
        }

        // Get client IP address for automatic population
        this.getClientIP();
    },

    methods: {
        async getClientIP() {
            try {
                const response = await fetch('https://api.ipify.org?format=json');
                const data = await response.json();
                this.lead.ip_address = data.ip;
            } catch (error) {
                console.warn('Could not fetch IP address:', error);
                this.lead.ip_address = 'Unknown';
            }
        },

        formatDOB() {
            let value = this.lead.dob.replace(/\D/g, '');
            if (value.length >= 2) {
                value = value.substring(0, 2) + '/' + value.substring(2);
            }
            if (value.length >= 5) {
                value = value.substring(0, 5) + '/' + value.substring(5, 9);
            }
            this.lead.dob = value;
        },

        calculateBMI() {
            if (!this.lead.weight || !this.lead.height_feet || !this.lead.height_inches) {
                return null;
            }
            
            const weightInKg = parseFloat(this.lead.weight) * 0.453592; // Convert lbs to kg
            const totalInches = (parseInt(this.lead.height_feet) * 12) + parseInt(this.lead.height_inches);
            const heightInM = totalInches * 0.0254; // Convert inches to meters
            
            return weightInKg / (heightInM * heightInM);
        },

        validateForm() {
            this.errors = {};
            this.validationErrors = [];

            // Required field validation
            const requiredFields = [
                'first_name', 'last_name', 'email', 'phone', 'gender', 
                'weight', 'desired_weight', 'height_feet', 'height_inches', 'dob', 
                'state'
            ];

            const fieldLabels = {
                'first_name': 'First Name',
                'last_name': 'Last Name',
                'email': 'Email',
                'phone': 'Phone',
                'gender': 'Gender',
                'weight': 'Current Weight',
                'desired_weight': 'Desired Weight',
                'height_feet': 'Height (feet)',
                'height_inches': 'Height (inches)',
                'dob': 'Date of Birth',
                'state': 'State'
            };

            requiredFields.forEach(field => {
                if (!this.lead[field] || this.lead[field].toString().trim() === '') {
                    this.errors[field] = 'This field is required';
                    this.validationErrors.push(`${fieldLabels[field]} is required`);
                }
            });

            // Email validation
            if (this.lead.email) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(this.lead.email)) {
                    this.errors.email = 'Please enter a valid email address';
                    this.validationErrors.push('Please enter a valid email address');
                }
            }

            // Phone validation
            if (this.lead.phone) {
                const cleanPhone = this.lead.phone.replace(/\D/g, '');
                if (cleanPhone.length !== 10) {
                    this.errors.phone = 'Phone number must be exactly 10 digits';
                    this.validationErrors.push('Phone number must be exactly 10 digits');
                }
            }

            // DOB validation
            if (this.lead.dob) {
                const dobRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
                if (!dobRegex.test(this.lead.dob)) {
                    this.errors.dob = 'Date must be in MM/DD/YYYY format';
                    this.validationErrors.push('Date of birth must be in MM/DD/YYYY format');
                } else {
                    const [month, day, year] = this.lead.dob.split('/');
                    const birthDate = new Date(year, month - 1, day);
                    const today = new Date();
                    
                    // More accurate age calculation considering month and day
                    let age = today.getFullYear() - birthDate.getFullYear();
                    const monthDiff = today.getMonth() - birthDate.getMonth();
                    
                    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                        age--;
                    }
                    
                    if (age < 18 || age > 80) {
                        this.errors.dob = 'Age must be between 18 and 80 years';
                        this.validationErrors.push('Age must be between 18 and 80 years');
                    }
                }
            }

            // Weight validation
            if (this.lead.weight) {
                const weight = parseInt(this.lead.weight);
                if (weight < 50 || weight > 800) {
                    this.errors.weight = 'Weight must be between 50 and 800 lbs';
                    this.validationErrors.push('Current weight must be between 50 and 800 lbs');
                }
            }

            if (this.lead.desired_weight) {
                const desiredWeight = parseInt(this.lead.desired_weight);
                if (desiredWeight < 50 || desiredWeight > 800) {
                    this.errors.desired_weight = 'Desired weight must be between 50 and 800 lbs';
                    this.validationErrors.push('Desired weight must be between 50 and 800 lbs');
                }
            }

            // Height validation
            if (this.lead.height_feet) {
                const feet = parseInt(this.lead.height_feet);
                if (feet < 3 || feet > 8) {
                    this.errors.height_feet = 'Height must be between 3 and 8 feet';
                    this.validationErrors.push('Height must be between 3 and 8 feet');
                }
            }
            
            if (this.lead.height_inches) {
                const inches = parseInt(this.lead.height_inches);
                if (inches < 0 || inches > 11) {
                    this.errors.height_inches = 'Inches must be between 0 and 11';
                    this.validationErrors.push('Height inches must be between 0 and 11');
                }
            }
            
            // Additional height validation - total height in inches
            if (this.lead.height_feet && this.lead.height_inches) {
                const totalInches = (parseInt(this.lead.height_feet) * 12) + parseInt(this.lead.height_inches);
                if (totalInches < 36 || totalInches > 96) {
                    this.errors.height_feet = 'Total height must be between 3\'0" and 8\'0"';
                    this.validationErrors.push('Total height must be between 3\'0" and 8\'0"');
                }
            }

            // BMI validation
            const bmi = this.calculateBMI();
            if (bmi) {
                const minBMI = this.lead.on_weight_loss_medication ? 20 : 25;
                if (bmi < minBMI) {
                    const medicationText = this.lead.on_weight_loss_medication ? 
                        'BMI must be at least 20 when taking weight loss medication' : 
                        'BMI must be at least 25. Please check "Currently taking weight loss medication" if applicable';
                    this.errors.weight = medicationText;
                    this.validationErrors.push(medicationText);
                }
            }

            return Object.keys(this.errors).length === 0;
        },

        async submitLead() {
            this.validateForm();
            
            if (this.validationErrors.length > 0) {
                // Clear any previous server errors when there are validation errors
                this.error = null;
                // Scroll to the error summary so user can see it
                this.$nextTick(() => {
                    const errorSummary = document.querySelector('.validation-error-summary');
                    if (errorSummary) {
                        errorSummary.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
                return;
            }

            this.submitting = true;
            this.error = null;

            try {
                // Convert feet and inches to total inches
                const totalInches = (parseInt(this.lead.height_feet) * 12) + parseInt(this.lead.height_inches);
                
                const leadData = {
                    first_name: this.lead.first_name.trim(),
                    last_name: this.lead.last_name.trim(),
                    email: this.lead.email.trim(),
                    phone: this.lead.phone.trim(),
                    gender: this.lead.gender,
                    weight: this.lead.weight.toString(),
                    desired_weight: this.lead.desired_weight.toString(),
                    heightInInches: totalInches,
                    dobMMDDYYYY: this.lead.dob,
                    form_type: 'call-center',
                    source_web: this.lead.source_web || 'call-center',
                    ip_address: this.lead.ip_address || 'Unknown',
                    state: this.lead.state
                };

                const response = await this.$http.post('/intake-form-callcenter', leadData);

                if (response.data.status === 'success') {
                    const userId = response.data.user.id;
                    
                    // Redirect to user details (charge) page, forwarding the agent id
                    this.$router.push(this.userDetailsRoute(userId));
                } else {
                    throw new Error(response.data.message || 'Failed to create lead');
                }
            } catch (error) {
                console.error('Error creating lead:', error);
                this.error = error.response?.data?.message || error.response?.data?.debug_error || error.message || 'Failed to create lead. Please try again.';
                
                // Scroll to the server error summary so user can see it
                this.$nextTick(() => {
                    const errorSummary = document.querySelector('.server-error-summary');
                    if (errorSummary) {
                        errorSummary.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
            } finally {
                this.submitting = false;
            }
        },

        async sendSignupLink() {
            this.sendingLink = true;
            this.linkStatus = null;
            try {
                const data = { phone: this.lead.phone.trim(), called_number: this.calledNumber };
                if (this.agentId) {
                    data.vici_agent = this.agentId;
                }
                await this.$http.post('/call-center/send-signup-link', data);
                this.linkStatus = 'success';
                this.linkStatusMessage = 'Signup link sent!';
            } catch (error) {
                console.error('Error sending signup link:', error);
                this.linkStatus = 'error';
                this.linkStatusMessage = error.response?.data?.message || 'Failed to send the link. Please try again.';
            } finally {
                this.sendingLink = false;
            }
        },

        resetForm() {
            this.lead = {
                first_name: '',
                last_name: '',
                email: '',
                phone: this.prefilledPhone,
                gender: '',
                weight: '',
                desired_weight: '',
                on_weight_loss_medication: false,
                height_feet: '',
                height_inches: '',
                dob: '',
                source_web: '',
                state: ''
            };
            this.errors = {};
            this.error = null;
            this.getClientIP();
        },

        // The charge page needs to know which agent handled the call, so carry
        // ?agent=<id> over from the /create-lead URL when it was provided.
        userDetailsRoute(userId) {
            const route = { path: `/users/${userId}` };
            if (this.agentId) {
                route.query = { agent: this.agentId };
            }
            return route;
        },

        goBack() {
            this.$router.push('/');
        },

        closeSuccessDialog() {
            this.showSuccessDialog = false;
            this.goBack();
        },

        createAnother() {
            this.showSuccessDialog = false;
            this.resetForm();
        },

        viewLead() {
            this.showSuccessDialog = false;
            if (this.createdLead.id) {
                this.$router.push(this.userDetailsRoute(this.createdLead.id));
            } else {
                this.goBack();
            }
        }
    }
}
</script>

<style scoped>
.create-lead-container {
    max-width: 1000px;
    margin: 0 auto;
    padding: 20px;
    font-family: 'Roboto', Arial, sans-serif;
}

.header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
}

.main-content {
    width: 100%;
}

.form-card {
    background-color: white;
    border-radius: 8px;
    padding: 32px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.md-card-title {
    font-size: 1.8rem;
    font-weight: 500;
    color: #333;
    margin: 0;
}

.access-denied {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 60px 32px;
}

.access-denied-icon {
    font-size: 48px;
    color: #757575;
    margin-bottom: 16px;
}

.access-denied h3 {
    margin: 0;
    font-size: 1.3rem;
    font-weight: 500;
    color: #555;
}

.md-card-subtitle {
    font-size: 1.3rem;
    font-weight: 500;
    color: #555;
    margin-top: 0;
    margin-bottom: 24px;
    border-bottom: 2px solid #e0e0e0;
    padding-bottom: 12px;
}

.lead-form {
    width: 100%;
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-bottom: 24px;
}

.form-row:last-of-type {
    grid-template-columns: 1fr;
}

.form-group {
    display: flex;
    flex-direction: column;
}

.form-group label {
    font-weight: 500;
    color: #555;
    margin-bottom: 8px;
    font-size: 0.9rem;
}

.md-input,
.md-select {
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 1rem;
    transition: border-color 0.2s, box-shadow 0.2s;
    background-color: white;
}

.md-input:focus,
.md-select:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
}

.md-input.invalid,
.md-select.invalid {
    border-color: #f44336;
    box-shadow: 0 0 0 2px rgba(244, 67, 54, 0.2);
}

.md-select {
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23666' d='M6 8L0 0h12z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 12px 8px;
    padding-right: 40px;
}

.error-text {
    color: #f44336;
    font-size: 0.8rem;
    margin-top: 4px;
    display: block;
}

.success-text {
    color: #4caf50;
    font-size: 0.8rem;
    margin-top: 4px;
    display: block;
}

.height-input-group {
    display: flex;
    gap: 12px;
    align-items: flex-end;
}

.height-field {
    display: flex;
    flex-direction: column;
    flex: 1;
    position: relative;
}

.height-label {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #666;
    font-size: 0.9rem;
    font-weight: 500;
    pointer-events: none;
}

.height-field .md-input {
    padding-right: 35px;
}

.checkbox-group {
    margin-top: 8px;
}

.checkbox-label {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 0.95rem;
    color: #555;
    font-weight: normal;
}

.checkbox-input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
}

.checkbox-custom {
    width: 20px;
    height: 20px;
    border: 2px solid #ddd;
    border-radius: 4px;
    margin-right: 12px;
    position: relative;
    transition: all 0.2s;
    background-color: white;
}

.checkbox-input:checked + .checkbox-custom {
    background-color: #2196F3;
    border-color: #2196F3;
}

.checkbox-input:checked + .checkbox-custom::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 6px;
    width: 6px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
}

.checkbox-label:hover .checkbox-custom {
    border-color: #2196F3;
}

.validation-error-summary {
    background-color: #ffebee;
    border: 1px solid #f44336;
    border-radius: 6px;
    padding: 20px;
    margin-bottom: 24px;
    color: #d32f2f;
}

.error-header {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
}

.error-header i {
    color: #f44336;
    margin-right: 8px;
    font-size: 20px;
}

.error-header h4 {
    margin: 0;
    font-size: 1rem;
    font-weight: 500;
    color: #d32f2f;
}

.error-list {
    margin: 0;
    padding-left: 20px;
}

.error-list li {
    margin-bottom: 6px;
    font-size: 0.9rem;
    line-height: 1.4;
}

.error-list li:last-child {
    margin-bottom: 0;
}

.server-error-summary {
    background-color: #ffebee;
    border: 1px solid #f44336;
    border-radius: 6px;
    padding: 20px;
    margin-bottom: 24px;
    color: #d32f2f;
}

.server-error-message {
    margin: 0;
    font-size: 0.9rem;
    line-height: 1.4;
}

.form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 16px;
    margin-top: 32px;
    padding-top: 24px;
    border-top: 1px solid #e0e0e0;
}

.md-btn {
    display: inline-flex;
    align-items: center;
    padding: 12px 24px;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: background-color 0.2s, box-shadow 0.2s;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    text-decoration: none;
}

.md-btn i {
    margin-right: 8px;
}

.md-btn:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.md-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    box-shadow: none;
}

.md-btn-primary {
    background-color: #2196F3;
    color: white;
}

.md-btn-secondary {
    background-color: #757575;
    color: white;
}

/* Loading and Error States */
.loading,
.error {
    text-align: center;
    padding: 40px;
}

.md-loader {
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    border-top: 4px solid #3498db;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 0 auto 15px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

.md-card-error {
    background-color: #ffebee;
    color: #d32f2f;
    display: flex;
    flex-direction: column;
    align-items: center;
}

/* Dialog Styles */
.dialog-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.md-dialog {
    background-color: white;
    border-radius: 8px;
    width: 90%;
    max-width: 500px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    max-height: 90vh;
    overflow: hidden;
}

.md-dialog-header {
    padding: 20px 24px;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.md-dialog-header h3 {
    margin: 0;
    font-weight: 500;
    font-size: 1.2rem;
    color: #333;
}

.md-dialog-close {
    background: transparent;
    border: none;
    cursor: pointer;
    color: #757575;
    padding: 4px;
    border-radius: 50%;
    display: flex;
}

.md-dialog-close:hover {
    background-color: #f1f1f1;
}

.md-dialog-body {
    padding: 24px;
    overflow-y: auto;
}

.md-dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 24px;
    border-top: 1px solid #eee;
}

.success-message {
    text-align: center;
}

.success-icon {
    font-size: 48px;
    color: #4CAF50;
    margin-bottom: 16px;
}

.success-message p {
    margin-bottom: 8px;
    color: #333;
}

/* Responsive Design */
@media (max-width: 768px) {
    .create-lead-container {
        padding: 15px;
    }

    .header-container {
        flex-direction: column;
        gap: 16px;
        align-items: stretch;
    }

    .form-card {
        padding: 24px;
    }

    .form-row {
        grid-template-columns: 1fr;
        gap: 16px;
        margin-bottom: 16px;
    }

    .form-actions {
        flex-direction: column-reverse;
    }

    .md-btn {
        width: 100%;
        justify-content: center;
    }
}

@media (max-width: 480px) {
    .form-card {
        padding: 16px;
    }

    .md-card-title {
        font-size: 1.4rem;
    }

    .md-card-subtitle {
        font-size: 1.1rem;
    }
}
</style>